import { useContext, useMemo, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { toast } from 'sonner';

import { UserContext } from '../Context/userContext';
import { submitContactForm } from '../../lib/contactService';
import { mapLoggedInUserForContact } from '../../lib/contactSentry';
import { ContactEmailFallback } from './ContactEmailFallback';

const INQUIRY_TYPES = [
  'Member Services',
  'CKA',
  'Continuing Education',
  'General',
];

// --- Inline styles (MUI-safe) ---
const s = {
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '4px',
    fontFamily: 'inherit',
  },
  field: {
    width: '100%',
    height: '38px',
    padding: '8px 12px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    boxShadow: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  select: {
    width: '100%',
    height: '38px',
    padding: '8px 32px 8px 12px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    boxShadow: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px 16px',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    boxShadow: 'none',
    resize: 'vertical',
    appearance: 'none',
    WebkitAppearance: 'none',
  },
  submitBtn: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: '6px',
    backgroundColor: '#213C72',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'none',
    transition: 'background-color 0.15s',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: '0',
    flexShrink: '0',
  },
  tabBtn: (active) => ({
    borderRadius: '4px',
    padding: '8px 4px',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: active ? '#213C72' : 'transparent',
    color: active ? '#fff' : '#374151',
    transition: 'background-color 0.15s, color 0.15s',
    boxShadow: 'none',
  }),
  spinner: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: '2px solid #fff',
    borderTopColor: 'transparent',
    animation: 'spin 0.6s linear infinite',
    display: 'inline-block',
    flexShrink: '0',
  },
  helpText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0',
    fontFamily: 'inherit',
  },
};

export default function ContactPopup() {
  const { state: userContext } = useContext(UserContext);
  const user = userContext?.user;
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    inquiryType: '',
    subject: '',
    message: '',
  });

  const handleOpen = () => {
    setContactForm((prev) => ({
      ...prev,
      name: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : prev.name,
      email: user?.email || prev.email,
    }));
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const contactSubmitMeta = useMemo(
    () => ({
      surface: 'popup',
      pathname: router.asPath,
      loggedInUser: mapLoggedInUserForContact(user),
    }),
    [router.asPath, user]
  );

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = { type: 'contact', ...contactForm };
    try {
      await submitContactForm(payload, contactSubmitMeta);
      toast.success('Message sent successfully!');
      setContactForm({ name: '', email: '', inquiryType: '', subject: '', message: '' });
      handleClose();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (router.pathname === '/members/contact' || router.pathname.startsWith('/staff/')) return null;

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 40,
              backgroundColor: 'rgba(0,0,0,0.2)',
              backdropFilter: 'blur(4px)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="popup"
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              zIndex: 50,
              width: '384px',
              maxWidth: 'calc(100vw - 3rem)',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#fff',
              padding: '24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Image
                  src="/static/images/ka-avatar.png"
                  alt="Kingdom Advisors"
                  width={40}
                  height={40}
                  style={{ borderRadius: '6px', display: 'block' }}
                />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0, fontFamily: 'inherit' }}>
                    Kingdom Advisors
                  </p>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, fontFamily: 'inherit' }}>
                    Contact Us
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                style={s.closeBtn}
                aria-label="Close"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <X size={16} color="#4b5563" />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <form onSubmit={handleContactSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={s.label}>Name</label>
                    <input
                      type="text"
                      style={s.field}
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label style={s.label}>Email</label>
                    <input
                      type="email"
                      style={s.field}
                      placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                      required
                    />
                  </div>
                  {/* 
                  Inquiry Type — Phase 2 (restore when Inquiry Type is added back)
                  <div>
                    <label style={s.label}>Inquiry Type</label>
                    <select
                      style={s.select}
                      value={contactForm.inquiryType}
                      onChange={(e) => setContactForm((p) => ({ ...p, inquiryType: e.target.value }))}
                      required
                    >
                      <option value="" disabled>Select an inquiry type</option>
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div> */}
                  <div>
                    <label style={s.label}>Subject</label>
                    <input
                      type="text"
                      style={s.field}
                      placeholder="Subject"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm((p) => ({ ...p, subject: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label style={s.label}>Message</label>
                    <textarea
                      rows={4}
                      style={s.textarea}
                      placeholder="How can we help you?"
                      value={contactForm.message}
                      onChange={(e) => setContactForm((p) => ({ ...p, message: e.target.value }))}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ ...s.submitBtn, opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'rgba(33,60,114,0.85)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#213C72'; }}
                  >
                    {isSubmitting
                      ? <span style={s.spinner} />
                      : <Send size={16} />}
                    Send Message
                  </button>
                  <ContactEmailFallback />
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button — hidden while popup is open */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            onClick={handleOpen}
            aria-label="Open contact form"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#213C72',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(33,60,114,0.85)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#213C72'; }}
          >
            <MessageCircle size={24} color="#fff" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
